import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users2, ArrowRight, MessageSquare, FileText, Users } from 'lucide-react';
import UserGroupManager from '@/components/manage_users/UserGroupManager';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

export default function MyGroups() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [myGroups, setMyGroups] = useState<any[]>([]);

  useEffect(() => {
    loadMyGroups();
  }, [user]);

  const loadMyGroups = async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase
        .from('user_group_members')
        .select('*, user_groups(*)')
        .eq('user_id', user.id);

      if (error) throw error;

      // Get counts for each group
      const enriched = await Promise.all(
        (data || []).map(async (membership) => {
          const { count: memberCount } = await supabase
            .from('user_group_members')
            .select('*', { count: 'exact', head: true })
            .eq('group_id', membership.group_id);

          const { count: discussionCount } = await supabase
            .from('group_discussions')
            .select('*', { count: 'exact', head: true })
            .eq('group_id', membership.group_id);

          const { count: resourceCount } = await supabase
            .from('group_resources')
            .select('*', { count: 'exact', head: true })
            .eq('group_id', membership.group_id);

          return {
            ...membership.user_groups,
            memberCount: memberCount || 0,
            discussionCount: discussionCount || 0,
            resourceCount: resourceCount || 0
          };
        })
      );

      setMyGroups(enriched);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
          <Users2 className="h-10 w-10" />
          My Professional Learning Communities
        </h1>
        <p className="text-muted-foreground text-lg">
          Collaborate with colleagues, share resources, and take courses together in your PLCs
        </p>
      </div>

      <Card className="mb-8 border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
        <CardHeader>
          <CardTitle className="text-lg">About Professional Learning Communities</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>
            Professional Learning Communities (PLCs) are collaborative groups where educators work together to:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-4 text-muted-foreground">
            <li>Share best practices and instructional strategies</li>
            <li>Take professional development courses together</li>
            <li>Collaborate on curriculum development and resources</li>
            <li>Support each other's professional growth</li>
            <li>Analyze student data and improve outcomes</li>
          </ul>
        </CardContent>
      </Card>

      {/* My Groups */}
      {myGroups.length > 0 && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">My Groups</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {myGroups.map((group) => (
              <Card key={group.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/groups/${group.id}`)}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="truncate">{group.name}</span>
                    <Badge variant="outline" className="capitalize">{group.group_type}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{group.description}</p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {group.memberCount}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageSquare className="h-3 w-3" />
                        {group.discussionCount}
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {group.resourceCount}
                      </span>
                    </div>
                  </div>
                  <Button size="sm" className="w-full">
                    View Group
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <h2 className="text-2xl font-bold mb-4">Manage Groups</h2>
      <UserGroupManager 
        onSave={() => {
          loadMyGroups();
        }} 
      />
    </div>
  );
}